import { observer } from "mobx-react";
import { useMemo } from "react";
import { PieChart, Pie, Cell, Tooltip } from "recharts";
import { PieChartTooltip } from "./PieChartToolTip";
import { CustomLabel } from "./CustomLabel";
import { PieChartItem, pieChartProps } from "./shared";
import { Text } from "@chakra-ui/react";
import { t } from "i18next";
import { PresenceType, TreatmentType } from "@keita/lanepatrol-sdk";
import { CraAttributeUtils } from "../../../../coding/utils/attribute-utils";
import { getBooleanColor } from "../../../../map/utils/map-utils";
import { useStores } from "../../../../shared/stores/RootStore";

interface Props {
  treatmentType: TreatmentType;
}

const TreatmentPieChartBase: React.FC<Props> = ({ treatmentType }) => {
  const { filterStore } = useStores();

  const data = useMemo(() => {
    const nodes = [...filterStore.filteredNodes];

    if (!nodes.length) return [];

    const totalNodes = nodes.length;

    let totalPresent = 0;
    let totalNotPresent = 0;

    nodes.forEach((node) => {
      const isPresent =
        node.treatments?.some((t) => t.type === treatmentType) ?? false;

      if (isPresent) {
        totalPresent++;
      } else {
        totalNotPresent++;
      }
    });

    const toDistanceKm = (n: number) =>
      ((n / totalNodes) * filterStore.filteredCraDistance) / 1000;

    return [
      {
        name: CraAttributeUtils.getOptionLabel(PresenceType.Present),
        value: toDistanceKm(totalPresent),
        color: getBooleanColor(true),
      },
      {
        name: CraAttributeUtils.getOptionLabel(PresenceType.NotPresent),
        value: toDistanceKm(totalNotPresent),
        color: getBooleanColor(false),
      },
    ].filter((item) => item.value) as PieChartItem[];
  }, [
    filterStore.filteredNodes,
    treatmentType,
    filterStore.filteredCraDistance,
  ]);

  if (!data.length)
    return (
      <Text fontSize="xs" alignSelf={"start"}>
        {t(`map:pieChart.noData`)}
      </Text>
    );

  return (
    <PieChart {...pieChartProps}>
      <Tooltip content={<PieChartTooltip />} />
      <Pie
        nameKey="name"
        dataKey="value"
        label={CustomLabel}
        data={data}
        outerRadius={80}
      >
        {data.map((item, i) => (
          <Cell key={i} fill={item.color} />
        ))}
      </Pie>
    </PieChart>
  );
};

export const TreatmentPieChart = observer(TreatmentPieChartBase);
